import { LinearLoader } from "../../../components/linear-loader/LinearLoader.jsx";
import { useEffect, useRef } from "react";
import { useSellerPageContext } from "../../../context-state/SellerPageContext.jsx";
import { useNavigate } from "react-router-dom";
import { ErrorService } from "../../../services/errorService.js";
import * as API from "../../../api/index.js";
import { isFutureEvent } from "../../../utils/sort.js";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getEndUserTokenKeyByHandler } from "../../../utils/endUser.js";
import { preloadFonts } from "../../../utils/fontLoader.js";
import { getMarketplaceLoader, getWhiteLabelHostname } from "../../../utils/market.js";
import { foreignMarketplaceUrl } from "../../../utils/marketplaceScope.js";
import { ForeignMarketplaceNotice } from "../ForeignMarketplaceNotice.jsx";
import { notifyFlutterUserStatus } from "../../../utils/flutterBridge.js";
import { updateI18nCurrency } from "../../../locales/i18n.js";
import { filterValidSubscriptions, getValidHrcCode } from "../../../utils/subscription.js";

export const SellerPublicPageGuard = ({ children }) => {
    const MarketplaceLoader = getMarketplaceLoader()
    const navigate = useNavigate();
    const navigateHome = () => navigate('/', { replace: true });
    const { accountContext, eventContext, subscriptionContext, endUserContext, diningContext } = useSellerPageContext()
    const { account, setAccount } = accountContext
    const { endUser, setEndUser, setPurchasedSubscriptions, setPurchasedEvents, triggerEndUserDataFetch, fetchingEndUserData, setFetchingEndUserData } = endUserContext
    const { setDining } = diningContext
    const { setEvents } = eventContext
    const { setSubscriptions } = subscriptionContext
    const [isLoading, setIsLoading] = useState(true)
    const { handler: sellerHandler } = useParams()

    // `getEndUserData` runs on mount AND on every triggerEndUserDataFetch — sign-in, profile
    // update, subscription purchase, the book-event modal. Those overlap, and responses can land
    // out of order: an earlier request finishing last would write the PREVIOUS session's data over
    // the current one. On a shared device that means one guest seeing another's dining balance.
    // Only the newest request is allowed to commit.
    const bootstrapGeneration = useRef(0)

    const getPublicSellerPageData = async () => {
        if (!sellerHandler) {
            return
        }
        try {
            const { data } = await API.getAccountIdByHandler(sellerHandler)
            setAccount(data)

            // Update i18n currency based on seller's currency
            if (data?.currency) {
                updateI18nCurrency(data.currency)
            }
        } catch (e) {
            console.log(e);
            ErrorService.handleError(e)
            return
        }
    }

    const getEndUserData = async () => {
        const generation = bootstrapGeneration.current + 1
        bootstrapGeneration.current = generation
        const isStale = () => bootstrapGeneration.current !== generation

        const token = localStorage.getItem(getEndUserTokenKeyByHandler(sellerHandler))
        localStorage.removeItem('endUserId')
        localStorage.removeItem('hrcCode')
        if (!token) {
            // Signed out: drop the credit with the session. Without this the header keeps offering
            // a dining link, and the balance of the previous guest stays in memory on a shared
            // device.
            setDining(null)
            // Notify Flutter that user is NOT logged in
            notifyFlutterUserStatus(false)
            return
        }

        try {
            setFetchingEndUserData(true)
            const { data } = await API.getEndUserDataByTokenAndSellerHandler({ token, sellerHandler })
            // A newer bootstrap started while this one was in flight — drop the whole response
            // rather than half-applying it over fresher state.
            if (isStale()) return

            setEndUser(data.endUser)
            // `null` for a non-member or switched-off credit, which is the whole gate: no dining
            // link, no dining page. `?? null` rather than `|| null` so the field's absence on an
            // older backend behaves identically to an explicit null.
            setDining(data.dining ?? null)
            // sort the events by start date
            setPurchasedEvents(data.userEvents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)))
            // sort by startDate date - show subscriptions with active status for My Account
            const activeStatusSubscriptions = data.seasonTickets
                .filter(sub => sub.status === 'active')
                .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
            setPurchasedSubscriptions(activeStatusSubscriptions)

            // Filter ONLY for HRC selection - valid subscriptions
            const validSubscriptions = filterValidSubscriptions(data.seasonTickets);
            const hrc = getValidHrcCode(validSubscriptions, data.endUser.selectedHrc);

            // for the event page
            localStorage.setItem('endUserId', data.endUser._id)
            localStorage.setItem('hrcCode', hrc)
            // Notify Flutter and parent window that user IS logged in
            notifyFlutterUserStatus(true, data.endUser)
        } catch (e) {
            // A stale request's failure says nothing about the current session. Reporting it would
            // tell the host app the buyer is signed out while a newer bootstrap is still running.
            if (isStale()) return
            console.log(e);
            // If error fetching user data, notify Flutter user is not logged in
            notifyFlutterUserStatus(false)
        } finally {
            // `return` inside `try` still runs `finally`, so without this a stale request clears
            // the flag belonging to the newer one — and consumers that gate on it (the dining page
            // decides whether to redirect on it) would act on a half-loaded session.
            if (!isStale()) setFetchingEndUserData(false)
        }
    }

    const loadFonts = async () => {
        try {
            // Preload fonts if they exist in the account design
            if (accountContext.account?.design) {
                const fontsToLoad = []
                if (accountContext.account.design.sellerPageFont && accountContext.account.design.sellerPageFontUrl) {
                    fontsToLoad.push({
                        family: accountContext.account.design.sellerPageFont,
                        url: accountContext.account.design.sellerPageFontUrl
                    })
                }
                if (accountContext.account.design.sellerPageTitleFont && accountContext.account.design.sellerPageTitleFontUrl) {
                    fontsToLoad.push({
                        family: accountContext.account.design.sellerPageTitleFont,
                        url: accountContext.account.design.sellerPageTitleFontUrl
                    })
                }

                if (fontsToLoad.length > 0) {
                    await preloadFonts(fontsToLoad)
                }
            }
        } catch (error) {
            console.error('Failed to load fonts:', error)
            // Continue loading the page even if fonts fail to load
        }
    }

    useEffect(() => {
        const init = async () => {
            setIsLoading(true)
            await Promise.all([getPublicSellerPageData(), getEndUserData()])
            await loadFonts()
            setIsLoading(false)
        }
        init()
    }, []);

    useEffect(() => {
        if (triggerEndUserDataFetch) {
            if (triggerEndUserDataFetch.token) {
                localStorage.setItem(getEndUserTokenKeyByHandler(sellerHandler), triggerEndUserDataFetch.token)
            }
            getEndUserData()
        }
    }, [triggerEndUserDataFetch])

    // Another marketplace's seller on this white-label domain: don't render its page (or let a
    // sign-in run against it) here — point to the seller's own site (#1407).
    const foreignUrl = isLoading ? null : foreignMarketplaceUrl({
        baseAppURL: account?.marketPlace?.baseAppURL,
        hostname: getWhiteLabelHostname(),
        pathname: window.location.pathname,
        search: window.location.search,
    })

    useEffect(() => {
        if (!foreignUrl) return
        // getEndUserData ran in parallel with the account fetch: a session left on this origin for
        // the foreign seller (signed in before this guard existed) must not leak into this site's
        // event pages (endUserId/hrcCode are global keys) or tell the app a foreign member is in.
        localStorage.removeItem(getEndUserTokenKeyByHandler(sellerHandler))
        localStorage.removeItem('endUserId')
        localStorage.removeItem('hrcCode')
        setEndUser(null)
        notifyFlutterUserStatus(false)
    }, [foreignUrl])

    if (isLoading) return <MarketplaceLoader />
    if (foreignUrl) return <ForeignMarketplaceNotice sellerName={account?.name} url={foreignUrl} />

    return children
}
